import { useMemo, useState } from "react";
import {
  type LifecycleStage,
  StatusBadge,
} from "@/components/status/StatusBadge";
import type {
  AnalysisResponse,
  PolizzaExtractResponse,
} from "@/lib/api/extract";
import { useMinLoading } from "@/lib/hooks/useMinLoading";
import { RunHistorySection } from "./RunHistorySection";
import { StatusResultSection } from "./StatusResultSection";
import {
  type DocumentKind,
  getRunFailurePresentation,
  getRunHeadline,
  type RunHistoryQuery,
  toKindLabel,
  type UnifiedRunHistoryItem,
  type UnifiedRunStatus,
} from "./status-domain";
import { useRunSelectionTransition } from "./useRunSelectionTransition";

type StatusTabProps = {
  runStatus: UnifiedRunStatus | null;
  analysis: AnalysisResponse | null;
  polizzaAnalysis: PolizzaExtractResponse | null;
  isPollingActive: boolean;
  isFetchingCompletedResult: boolean;
  completedResultError: string | null;
  errorMessage: string | null;
  runs: UnifiedRunHistoryItem[];
  totalRuns: number;
  pageCount: number;
  runsQuery: RunHistoryQuery;
  refreshingRuns: boolean;
  downloadingPdfRunId: number | null;
  hasActiveRuns: boolean;
  onRunsQueryChange: (
    updater: (prev: RunHistoryQuery) => RunHistoryQuery,
    historyMode?: "push" | "replace",
  ) => void;
  onRefreshRuns: () => Promise<void>;
  onSelectRun: (runId: number, kind: DocumentKind) => Promise<void>;
  onDeleteRun: (runId: number, kind: DocumentKind) => Promise<void>;
  onDownloadRunPdf: (runId: number, kind: DocumentKind) => Promise<void>;
  onRetryCompletedResult: () => Promise<void>;
  onGoToUpload: () => void;
};

function toLifecycleStage(
  status: UnifiedRunStatus["status"] | null,
): LifecycleStage {
  if (status === "queued") return "queued";
  if (status === "processing") return "processing";
  if (status === "completed") return "completed";
  if (status === "failed") return "failed";
  return "uploaded";
}

export function StatusTabPlaceholder({
  runStatus,
  analysis,
  polizzaAnalysis,
  isPollingActive,
  isFetchingCompletedResult,
  completedResultError,
  errorMessage,
  runs,
  totalRuns,
  pageCount,
  runsQuery,
  refreshingRuns,
  downloadingPdfRunId,
  hasActiveRuns,
  onRunsQueryChange,
  onRefreshRuns,
  onSelectRun,
  onDeleteRun,
  onDownloadRunPdf,
  onRetryCompletedResult,
  onGoToUpload,
}: StatusTabProps) {
  const [pdfErrorMessage, setPdfErrorMessage] = useState<string | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { loading: minRunsRefreshLoading, run: runRunsRefresh } = useMinLoading(
    { minMs: 1000 },
  );
  const refreshRunsLoading = refreshingRuns || minRunsRefreshLoading;
  const stage = toLifecycleStage(runStatus?.status ?? null);
  const totalPages = runStatus?.totalPages ?? 0;
  const processedPages = runStatus?.processedPages ?? 0;
  const failedPages = runStatus?.failedPages ?? 0;
  const progressPct =
    typeof runStatus?.progressPct === "number"
      ? Math.min(100, Math.max(0, runStatus.progressPct))
      : totalPages > 0
        ? Math.min(100, (processedPages / totalPages) * 100)
        : 0;

  const { beginRunSelection, isPendingRun, isResultTransitioning } =
    useRunSelectionTransition({
      analysis,
      errorMessage,
      polizzaAnalysis,
      runStatus,
    });

  const canDownloadCurrentPdf = Boolean(
    runStatus &&
      runStatus.kind === "fatture_ddt" &&
      runStatus.status === "completed" &&
      analysis?.pdf_base64,
  );
  const isDownloadingCurrentPdf = Boolean(
    runStatus && downloadingPdfRunId === runStatus.id,
  );
  const isProcessingRun =
    runStatus?.status === "queued" || runStatus?.status === "processing";
  const showPollingMessage = Boolean(
    runStatus && isProcessingRun && isPollingActive,
  );
  const effectiveRunId = runStatus ? `#${runStatus.id}` : null;
  const failurePresentation = getRunFailurePresentation(runStatus);

  const analysisDetailsJson = useMemo(() => {
    if (!isDetailsOpen) return "";
    if (runStatus?.kind === "polizza_carico" && polizzaAnalysis) {
      return JSON.stringify(polizzaAnalysis, null, 2);
    }
    if (runStatus?.kind === "fatture_ddt" && analysis) {
      return JSON.stringify(analysis, null, 2);
    }
    return "";
  }, [analysis, isDetailsOpen, polizzaAnalysis, runStatus?.kind]);

  function scrollToPageTop() {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  }

  async function handlePdfDownload(runId: number, kind: DocumentKind) {
    try {
      setPdfErrorMessage(null);
      await onDownloadRunPdf(runId, kind);
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim()
          ? error.message
          : "Download PDF non riuscito.";
      setPdfErrorMessage(message);
    }
  }

  return (
    <section className="panel-screen">
      <header className="panel-screen__header">
        <h2 className="panel-screen__title">Stato e storico delle analisi</h2>
        <p className="panel-screen__description">
          Monitora fatture/DDT e polizze di carico, verifica errori e riapri le
          elaborazioni recenti.
        </p>
      </header>
      <div className="panel-screen__body status-panel">
        <section
          className="status-panel__card"
          aria-label="Stato analisi corrente"
        >
          <div className="status-panel__line">
            <StatusBadge stage={stage} />
            <span className="status-panel__summary status-panel__live-value">
              {getRunHeadline(runStatus)}
            </span>
            {runStatus ? (
              <span className="status-panel__kind-pill">
                {toKindLabel(runStatus.kind)}
              </span>
            ) : null}
          </div>

          {runStatus ? (
            <div
              className="status-panel__progress-wrap"
              role="progressbar"
              aria-label="Progresso analisi"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Number(progressPct.toFixed(1))}
            >
              <div className="status-panel__progress-track">
                <div
                  className={`status-panel__progress-fill status-panel__progress-fill--${runStatus.status}`}
                  style={{
                    transform: `scaleX(${(progressPct / 100).toFixed(4)})`,
                  }}
                />
              </div>
            </div>
          ) : null}

          {showPollingMessage ? (
            <p className="status-panel__polling text-sm text-muted-foreground">
              <span className="status-panel__polling-dot" aria-hidden="true" />
              Aggiornamento automatico in corso…
            </p>
          ) : null}
          {runStatus ? (
            <>
              <p className="text-sm text-muted-foreground status-panel__live-meta">
                Pagine processate: {processedPages}/{totalPages} · Pagine
                fallite: {failedPages}
              </p>
              <p className="text-sm text-muted-foreground status-panel__live-meta">
                File: {runStatus.inputFilename ?? "n.d."}
                {runStatus.renderDpiUsed
                  ? ` · Render DPI: ${runStatus.renderDpiUsed}`
                  : ""}
              </p>
              {failurePresentation ? (
                <p className="status-panel__live-status-note">
                  {failurePresentation.scope === "document"
                    ? "Errore documento rilevato"
                    : "Sono presenti pagine con errore"}
                </p>
              ) : null}
            </>
          ) : null}
          {errorMessage ? (
            <p className="upload-form__error" role="alert">
              Errore: {errorMessage}
            </p>
          ) : null}
        </section>

        <div>
          <StatusResultSection
            analysis={analysis}
            polizzaAnalysis={polizzaAnalysis}
            runStatus={runStatus}
            effectiveRunId={effectiveRunId}
            isResultTransitioning={isResultTransitioning}
            isFetchingCompletedResult={isFetchingCompletedResult}
            completedResultError={completedResultError}
            canDownloadCurrentPdf={canDownloadCurrentPdf}
            isDownloadingCurrentPdf={isDownloadingCurrentPdf}
            pdfErrorMessage={pdfErrorMessage}
            analysisDetailsJson={analysisDetailsJson}
            onDetailsOpenChange={setIsDetailsOpen}
            onRetryCompletedResult={onRetryCompletedResult}
            onDownloadCurrentPdf={handlePdfDownload}
            onGoToUpload={onGoToUpload}
          />
        </div>

        <RunHistorySection
          runs={runs}
          totalRuns={totalRuns}
          pageCount={pageCount}
          query={runsQuery}
          refreshingRuns={refreshRunsLoading}
          downloadingPdfRunId={downloadingPdfRunId}
          activeRun={runStatus}
          hasActiveRuns={hasActiveRuns}
          onQueryChange={onRunsQueryChange}
          onRefreshRuns={() => runRunsRefresh(onRefreshRuns)}
          onSelectRun={onSelectRun}
          onDeleteRun={onDeleteRun}
          onDownloadRunPdf={handlePdfDownload}
          onBeginRunSelection={beginRunSelection}
          isRunPending={isPendingRun}
          onAfterSelectRun={scrollToPageTop}
        />
      </div>
    </section>
  );
}
